import axios from 'axios'
import isDesktopApp from '@/desktop-app/isDesktopApp.js'
import i18n from '@/lang/lang.js'
import desktopApp from '@/desktop-app/desktopApp.js'
import ipcCommandsEnum from '@/desktop-app/ipcCommandsEnum.js'
import DataStore from '@/api/DataStore/DataStore.js'
import {isArray} from '@/lib/data/Validations/utils/Utils.js'
import {clone, isMobile} from '@/lib/helper/helper.js'
import SocketCommand from '@/api/SocketCommand.js'

const IS_DESKTOP_APP = isDesktopApp()
const desktopBridge = desktopApp()

/**
 *
 * @param payload {Object}
 */
function notifyPrintCommandReceived(payload) {
  if (!canNotifyPrintCommandReceived(payload)) return
  window.callAS(SocketCommand.Order.PrintNotify, payload)
}

function canNotifyPrintCommandReceived(payload) {
  const appConfig = window.Vue?.AppConfig || window.appConfig
  const APP_LOGGING = appConfig?.APP_LOGGING
  const APP_LOGGING_PRINT = APP_LOGGING?.PRINT
  const isAppLoggingActive = !!APP_LOGGING?.ENABLED
  const isAppPrintLogActive = isAppLoggingActive && IS_DESKTOP_APP && !!APP_LOGGING_PRINT?.ENABLED
  //const isAppPrintLogVisible = isAppPrintLogActive && !!APP_LOGGING_PRINT?.VISIBLE

  const typeIsEnabled = APP_LOGGING_PRINT && APP_LOGGING_PRINT[payload.Type]

  if (!IS_DESKTOP_APP || !isAppPrintLogActive || !typeIsEnabled) return false

  return true
}

function getPrintLocale() {
  const userLocale = window?.Vue?.$i18n?.locale || import.meta.env.VITE_I18N_LOCALE || import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'el'
  const usePrintDefaultLanguage = window?.appConfig?.LOCATION_DATA?.UsePrintDefaultLanguage ?? false
  const printDefaultLanguage = window?.appConfig?.LOCATION_DATA?.PrintDefaultLanguage || 12
  const printDefaultLanguageLocale = i18n.languages.find(l => parseInt(l.id) === parseInt(printDefaultLanguage))?.locale || userLocale

  return usePrintDefaultLanguage ? printDefaultLanguageLocale : userLocale
}

/**
 *
 * @param url {String}
 * @param printers {Array}
 * @param auto {Boolean}
 * @param printNotify {Object}
 */
function printURLtoPrinter(url, printers = [], auto = false, printNotify = null) {
  if (printNotify && auto) notifyPrintCommandReceived(printNotify)

  if (IS_DESKTOP_APP) {
    desktopBridge.send(ipcCommandsEnum.PrintUrlToPrinter, url, printers, auto, window.authorizationTokenBearer, getPrintLocale())
  } else {
    if (auto) return
    API.get(url, {})
      .then(response => {
        printRawHTMLtoPrinter(response.data, printers)
      })
      .catch(e => {
        console.error(e)
      })
  }
}

/**
 *
 * @param htmlString {String}
 * @param printers {Array}
 */
function printRawHTMLtoPrinter(htmlString, printers = []) {
  if (IS_DESKTOP_APP) {
    desktopBridge.send(ipcCommandsEnum.PrintRawHtmlToPrinter, htmlString, printers)
  } else {
    const win = window.open('', 'Print', 'height=600,width=800')
    setTimeout(() => {
      if (win) {
        win.document.write('<html><head><title>Print</title>')
        win.document.write('</head><body>')
        win.document.write(htmlString)
        win.document.write('</body></html>')
        win.document.close()
      }
    }, 450)

    setTimeout(() => {
      if (win) {
        win.focus()
        win.print()
      }
    }, 650)

    setTimeout(() => {
      if (isMobile()) {
        if (win) {
          win.onfocus = () => {
            win.close()
          }
        }
      } else {
        if (win) {
          win.close()
        }
      }
    }, 700)
  }
}

function canPrintToMachine(guid) {
  let retVal = true
  const ownMachineGroups = window?.Vue?.appMachineOwnGroups || []

  if (guid && isArray(guid) && guid?.length) {
    retVal = guid.includes(DataStore.app.guid) || ownMachineGroups.some(machineGroupId => guid.map(c => String(c)).includes(String(machineGroupId)))
  }

  return retVal
}

function canPrintToPrinterGroup(printer, printerGroup) {
  if (!printer || !printerGroup || !printerGroup?.Status) return false

  return printer?.groups?.includes(printerGroup?.Id)
}

function canPrintTemplate(printer, templateId) {
  if (!printer || !templateId) return false

  return printer?.templates?.includes(templateId)
}

function canPrintOrderType(printer, orderTypeId) {
  if (!printer) return false

  if (Array.isArray(printer?.orderTypes) && printer?.orderTypes?.length > 0) {
    return printer?.orderTypes?.includes(parseInt(orderTypeId))
  }

  return true
}

function canPrintOrderSourceType(printer, sourceTypeId, isScheduled) {
  if (!printer) return false

  if (Array.isArray(printer?.scheduled) && printer?.scheduled?.length > 0 && isScheduled) {
    return printer?.scheduled?.includes(sourceTypeId)
  }

  if (Array.isArray(printer?.sources) && printer?.sources?.length > 0) {
    return printer?.sources?.includes(sourceTypeId)
  }

  return true
}

/**
 *
 * @type {{printTableReceiptAuto: Printer.printTableReceiptAuto, printOrder: Printer.printOrder, printTableOrderAuto: Printer.printTableOrderAuto, printOrderPreparation: Printer.printOrderPreparation, printPreparationAuto: Printer.printPreparationAuto, printStaffShift: Printer.printStaffShift, printStatsProducts: Printer.printStatsProducts, printOrderAuto: Printer.printOrderAuto, printTableReceiptFromOrder: Printer.printTableReceiptFromOrder, printTableOrderPreparationAuto: Printer.printTableOrderPreparationAuto, printReceiptTotalsZ: Printer.printReceiptTotalsZ, printReceipt: Printer.printReceipt, printTableCanceled: Printer.printTableCanceled, printTableOrderPreparation: Printer.printTableOrderPreparation, printTableOrder: Printer.printTableOrder, getSystemPrinters: (function(): *), printTableReceipt: Printer.printTableReceipt, printTableCanceledAuto: Printer.printTableCanceledAuto, printReceiptAuto: Printer.printReceiptAuto, printOrderTotalsZ: Printer.printOrderTotalsZ}}
 */
export const Printer = {
  /**
   *
   * @returns {Array}
   */
  getSystemPrinters: () => {
    return desktopBridge.sendSync(ipcCommandsEnum.GetSystemPrinters) || []
  },

  /**
   *
   * @param orderId {Number}
   * @param guid {Array | String}
   * @param sourceTypeId {Number | String}
   * @param isScheduled {Boolean}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   * @param orderTypeId {Number | String}
   */
  printOrderAuto: (orderId, guid, sourceTypeId, isScheduled, orderPrintId, orderPrintType, orderTypeId) => {
    // console.log('>>>> printOrderAuto::: ', orderId, guid)
    if (!canPrintToMachine(guid)) return

    const printersArray = window.Vue.AppMachinePrinters
      .filter(printer => printer.status === '1' && parseInt(printer.copies) > 0)
      .filter(printer => canPrintOrderSourceType(printer, sourceTypeId, isScheduled))
    //TODO .filter(printer => canPrintOrderType(printer, orderTypeId))

    const printers = clone(printersArray || [])
    const url = `${import.meta.env.VITE_API_URL}order/print/${orderId}`
    const printNotifyPayload = {
      OrderPrintId: orderPrintId,
      Type        : orderPrintType,
      Payload     : {OrderId: orderId, Guid: guid, Printers: printers, Url: url}
    }

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true, printNotifyPayload)
    }
  },

  /**
   *
   * @param orderId {Number}
   * @param printerGroupId {Number}
   * @param guid {Array | String}
   * @param sourceTypeId {Number | String}
   * @param isScheduled {Boolean}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   * @param orderTypeId {Number | String}
   */
  printPreparationAuto: (orderId, printerGroupId, guid, sourceTypeId, isScheduled, orderPrintId, orderPrintType, orderTypeId) => {
    // console.log('>>>> printPreparationAuto::: ', orderId, printerGroupId, guid)
    if (!canPrintToMachine(guid)) return

    const printerGroup = window.Vue.appPrinterGroups?.find(group => parseInt(group.Id) === parseInt(printerGroupId))
    if (!printerGroup || !window?.Vue?.AppConfig?.LOCATION_DATA?.PreparationDocumentEnabled) return

    const printersArray = window.Vue.AppMachinePreparationPrinters
      .filter(printer => printer.status === '1' && parseInt(printer.copies) > 0)
      .filter(printer => canPrintToPrinterGroup(printer, printerGroup))
      .filter(printer => canPrintOrderSourceType(printer, sourceTypeId, isScheduled))
    //TODO .filter(printer => canPrintOrderType(printer, orderTypeId))

    const printers = clone(printersArray || [])
    const url = `${import.meta.env.VITE_API_URL}order/preparation/print/${orderId}/${printerGroupId}`
    const printNotifyPayload = {
      OrderPrintId: orderPrintId,
      Type        : orderPrintType,
      Payload     : {OrderId: orderId, PrinterGroup: printerGroup, Guid: guid, Printers: printers, Url: url}
    }

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true, printNotifyPayload)
    }
  },

  /**
   *
   * @param menuId {Number}
   * @param templateId {Number}
   * @param guid {Array | String}
   * @param sourceTypeId {Number | String}
   * @param isScheduled {Boolean}
   */
  printMenuMarketingAuto: (menuId, templateId, guid, sourceTypeId, isScheduled) => {
    // console.log('>>>> printMarketingAuto::: ', menuId, templateId, guid)
    if (!canPrintToMachine(guid) || !window?.Vue?.AppConfig?.LOCATION_DATA?.MarketingDocumentEnabled) return

    const printersArray = window.Vue.AppMachineMarketingPrinters
      .filter(printer => printer.status === '1' && parseInt(printer.copies) > 0)
      .filter(printer => canPrintTemplate(printer, templateId))
      .filter(printer => canPrintOrderSourceType(printer, sourceTypeId, isScheduled))

    const printers = clone(printersArray || [])
    const url = `${import.meta.env.VITE_API_URL}menu/marketing/print/${menuId}`

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true)
    }
  },

  /**
   *
   * @param receiptId {Number | String}
   * @param guid {Array | String}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   */
  printReceiptAuto: (receiptId, guid, orderPrintId, orderPrintType) => {
    // console.log('>>>> printReceiptAuto::: ', receiptId, guid)
    if (!canPrintToMachine(guid)) return

    const printersArray = window.Vue.AppMachineFiscalDocPrinters.filter(printer => printer.status === '1') || []
    const printers = clone(printersArray)
    const url = `${import.meta.env.VITE_API_URL}receipt/print/${receiptId}`
    const printNotifyPayload = {
      OrderPrintId: orderPrintId,
      Type        : orderPrintType,
      Payload     : {ReceiptId: receiptId, Guid: guid, Printers: printers, Url: url}
    }

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true, printNotifyPayload)
    }
  },

  /**
   *
   * @param cartId {Number | String}
   * @param timestamp {String}
   * @param guid {Array | String}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   */
  printTableOrderAuto: (cartId, timestamp, guid, orderPrintId, orderPrintType) => {
    // console.log('>>>> printOrderAuto::: ', cartId, timestamp, guid)
    if (!canPrintToMachine(guid)) return

    const printersArray = window.Vue.AppMachinePrinters.filter(printer => printer.status === '1' && parseInt(printer.copies) > 0) || []
    const printers = clone(printersArray)
    const url = `${import.meta.env.VITE_API_URL}table/order/print/${cartId}/${timestamp || 0}`
    const printNotifyPayload = {
      OrderPrintId: orderPrintId,
      Type        : orderPrintType,
      Payload     : {CartId: cartId, Timestamp: timestamp, Guid: guid, Printers: printers, Url: url}
    }

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true, printNotifyPayload)
    }
  },

  /**
   *
   * @param cartId {Number | String}
   * @param printerGroupId {Number | String}
   * @param timestamp {String}
   * @param guid {Array | String}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   */
  printTableOrderPreparationAuto: (cartId, printerGroupId, timestamp, guid, orderPrintId, orderPrintType) => {
    // console.log('>>>> printTableOrderPreparationAuto::: ', cartId, printerGroupId, timestamp, guid)
    if (!canPrintToMachine(guid)) return

    const printerGroup = window.Vue.appPrinterGroups?.find(group => parseInt(group.Id) === parseInt(printerGroupId))
    if (!printerGroup || !window?.Vue?.AppConfig?.LOCATION_DATA?.PreparationDocumentEnabled) return

    const printersArray = window.Vue.AppMachinePreparationPrinters.filter(printer => printerGroup?.Status && printer?.groups?.includes(printerGroupId) && printer.status === '1' && parseInt(printer.copies) > 0) || []
    const printers = clone(printersArray)
    const url = `${import.meta.env.VITE_API_URL}table/order/preparation/print/${cartId}/${printerGroupId}/${timestamp}`
    const printNotifyPayload = {
      OrderPrintId: orderPrintId,
      Type        : orderPrintType,
      Payload     : {CartId: cartId, PrinterGroup: printerGroup, Timestamp: timestamp, Guid: guid, Printers: printers, Url: url}
    }

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true, printNotifyPayload)
    }
  },

  /**
   *
   * @param cartId {Number | String}
   * @param timestamp {String}
   * @param guid {Array | String}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   */
  printTableReceiptAuto: (cartId, timestamp, guid, orderPrintId, orderPrintType) => {
    // console.log('>>>> printTableReceiptAuto::: ', cartId, timestamp, guid)
    if (!canPrintToMachine(guid) || !window?.Vue?.userCanViewFiscalDoc) return

    const printersArray = window.Vue.AppMachineFiscalDocPrinters.filter(printer => printer.status === '1' && parseInt(printer.copies) > 0) || []
    const printers = clone(printersArray)
    const url = `${import.meta.env.VITE_API_URL}table/receipt/print/${cartId}/${timestamp}`
    const printNotifyPayload = {
      OrderPrintId: orderPrintId,
      Type        : orderPrintType,
      Payload     : {CartId: cartId, Timestamp: timestamp, Guid: guid, Printers: printers, Url: url}
    }

    if (printers.length > 0) {
      printURLtoPrinter(url, printers, true, printNotifyPayload)
    }
  },

  /**
   *
   * @param cartId {Number | String}
   * @param items {Array}
   * @param guid {Array | String}
   * @param orderPrintId {Number | String}
   * @param orderPrintType {PrintNotifyTypeEnum}
   */
  printTableCanceledAuto: (cartId, items, guid, orderPrintId, orderPrintType) => {
    // console.log('>>>> printTableCanceledAuto::: ', cartId, items, guid)
    if (!canPrintToMachine(guid)) return

    const printersArray = window.Vue.AppMachinePrinters.filter(printer => printer.status === '1' && parseInt(printer.copies) > 0) || []
    const printers = clone(printersArray)

    if (items && Array.isArray(items) && items?.length) {
      const itemsString = JSON.stringify(items)
      const url = `${import.meta.env.VITE_API_URL}table/order/cancel/print/${cartId}/${itemsString}`
      const printNotifyPayload = {
        OrderPrintId: orderPrintId,
        Type        : orderPrintType,
        Payload     : {CartId: cartId, Items: items, Guid: guid, Printers: printers, Url: url}
      }

      if (printers.length > 0) {
        printURLtoPrinter(url, printers, true, printNotifyPayload)
      }
    }
  },

  /**
   *
   * @param orderId {Number | String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printOrder: (orderId, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printOrder::: ', orderId, printer.name, copies)
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    printURLtoPrinter(`${import.meta.env.VITE_API_URL}order/print/${orderId}`, [printer], false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.Notification'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param orderId {Number | String}
   * @param printerGroupId {Number | String}
   * @param copies {Number}
   */
  printOrderPreparation: (orderId, printerGroupId, copies = 1) => {
    // console.log('>>>>>>>> printOrderPreparation::: ', orderId, printerGroupId, copies)
    const printerGroup = window.Vue.appPrinterGroups?.find(group => parseInt(group.Id) === parseInt(printerGroupId))
    if (!printerGroup || !window?.Vue?.AppConfig?.LOCATION_DATA?.PreparationDocumentEnabled) return

    const printersArray = window.Vue.AppMachinePreparationPrinters.filter(printer => printerGroup?.Status && printer?.groups?.includes(printerGroupId) && printer.status === '1') || []
    const printers = clone(printersArray)
    let printer = IS_DESKTOP_APP ? printers : [{
      name  : '',
      copies: copies
    }]
    if (IS_DESKTOP_APP && !printer.length) return

    printer = printer.map(p => {
      p.copies = parseInt(copies)
      return p
    })

    printURLtoPrinter(`${import.meta.env.VITE_API_URL}order/preparation/print/${orderId}/${printerGroupId}`, printer, false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.Notification'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param menuId {Number | String}
   * @param templateId {Number | String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printMenuMarketing: (menuId, templateId, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printOrderMarketing::: ', menuId, templateId, copies)
    if (!window?.Vue?.AppConfig?.LOCATION_DATA?.MarketingDocumentEnabled) return

    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)

    printURLtoPrinter(`${import.meta.env.VITE_API_URL}menu/marketing/print/${menuId}`, [printer], false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.Notification'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param receiptId {Number | String}
   * @param guid {Array | String}
   * @param orderPrintId {Number | String}
   */
  printReceipt: (receiptId, guid, orderPrintId) => {
    // console.log('>>>>>>>> printReceipt::: ', orderId, guid)

    if (!canPrintToMachine(guid) || !window?.Vue?.userCanViewFiscalDoc) return

    const printersArray = window.Vue.AppMachineFiscalDocPrinters.filter(printer => printer.status === '1') || []
    const printers = clone(printersArray)

    printURLtoPrinter(`${import.meta.env.VITE_API_URL}receipt/print/${receiptId}`, printers, false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.NotificationReceipt'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param range {String}
   * @param staffId {String|Number}
   * @param guid {String|Number}
   * @param withoutOpenHours {Boolean}
   * @param printer {Object}
   * @param copies {Number}
   */
  printReceiptTotalsZ: (range, staffId, guid, withoutOpenHours = false, printer = {name: ''}, copies = 1) => {
    if (!window?.Vue?.userCanViewFiscalDoc) return

    // console.log('>>>>>>>> printReceiptTotalsZ::: ', range, staffId, withoutOpenHours, printer.name, copies)

    range = range || '-'
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    printURLtoPrinter(`${import.meta.env.VITE_API_URL}receipt/totals/print/${withoutOpenHours ?? false}/${staffId ?? null}/${guid ?? null}/${range}`, [printer], false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.NotificationTotals'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param range {String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printOrderTotalsZ: (range, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printOrderTotalsZ::: ', range, printer.name, copies)
    range = range || '-'
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    printURLtoPrinter(`${import.meta.env.VITE_API_URL}order/totals/print/${range}`, [printer], false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.NotificationTotals'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param cartId {Number | String}
   * @param timestamp {String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printTableOrder: (cartId, timestamp, printer = {name: ''}, copies = 1, showNotification = true) => {
    // console.log('>>>>>>>> printTableOrder::: ', cartId, timestamp, printer.name, copies)
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    if (timestamp) {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/print/${cartId}/${timestamp}`, [printer], false)
    } else {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/print/${cartId}/0`, [printer], false)
    }

    if (!showNotification) return

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.Notification'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param printer {Object}
   * @param copies {Number}
   */
  printOpenTablesReport: (printer = {name: ''}, copies = 1) => {
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)

    printURLtoPrinter(`${import.meta.env.VITE_API_URL}tables/report/print`, [printer], false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Restaurant.Tables.Notification.OpenTablesReportSendForPrint'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param cartId {Number | String}
   * @param printerGroupId {Number | String}
   * @param timestamp {String}
   * @param copies {Number}
   */
  printTableOrderPreparation: (cartId, printerGroupId, timestamp, copies = 1) => {
    // console.log('>>>>>>>> printTableOrderPreparation::: ', cartId, printerGroupId, timestamp, copies)
    const printerGroup = window.Vue.appPrinterGroups?.find(group => parseInt(group.Id) === parseInt(printerGroupId))
    if (!printerGroup || !window?.Vue?.AppConfig?.LOCATION_DATA?.PreparationDocumentEnabled) return

    const printersArray = window.Vue.AppMachinePreparationPrinters.filter(printer => printerGroup?.Status && printer?.groups?.includes(printerGroupId) && printer.status === '1') || []
    const printers = clone(printersArray)
    const printer = IS_DESKTOP_APP ? printers : [{name: ''}]
    if (IS_DESKTOP_APP && !printer.length) return

    printer.copies = parseInt(copies)

    if (timestamp) {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/preparation/print/${cartId}/${printerGroupId}/${timestamp}`, [printer], false)
    } else {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/preparation/print/${cartId}/${printerGroupId}/0`, [printer], false)
    }

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.Notification'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param receiptType {FiscalDocTypeEnum}
   * @param cartId {Number | String}
   * @param timestamp {String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printTableReceipt: (receiptType, cartId, timestamp, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printTableReceipt::: ', receiptType, cartId, timestamp, printer.name, copies)
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    if (timestamp) {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/receipt/print/${cartId}/${receiptType}/${timestamp}`, [printer], false)
    } else {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/receipt/print/${cartId}/${receiptType}/0`, [printer], false)
    }

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.NotificationReceipt'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param orderId {Number | String}
   * @param timestamp {String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printTableReceiptFromOrder: (orderId, timestamp, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printTableReceiptFromOrder::: ', orderId, timestamp, printer.name, copies)
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    if (timestamp) {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/receipt/print/${orderId}/${timestamp}`, [printer], false)
    } else {
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/receipt/print/${orderId}/0`, [printer], false)
    }

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.NotificationReceipt'),
      type: 'info',
      icon: 'print'
    })
  },

  /**
   *
   * @param cartId {Number | String}
   * @param items {Array}
   * @param printer {Object}
   * @param copies {Number}
   */
  printTableCanceled: (cartId, items, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printTableCanceled::: ', cartId, items, printer.name, copies)
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    if (items && Array.isArray(items) && items?.length) {
      const itemsString = JSON.stringify(items)
      printURLtoPrinter(`${import.meta.env.VITE_API_URL}table/order/cancel/print/${cartId}/${itemsString}`, [printer], false)

      window.Vue && window.Vue.$bus.$emit('app-show-notification', {
        body: window.Vue.$t('Order.Print.Notification'),
        type: 'info',
        icon: 'print'
      })
    }
  },

  /**
   *
   * @param htmlString {String}
   * @param printerName {String}
   * @param copies {Number}
   */
  printStaffShift: (htmlString, printerName = '', copies = 1) => {
    if (IS_DESKTOP_APP && printerName === '') return
    printRawHTMLtoPrinter(htmlString, [{
      name  : printerName,
      copies: parseInt(copies)
    }])
  },

  /**
   *
   * @param range {String}
   * @param sortBy {String}
   * @param orderBy {String}
   * @param printer {Object}
   * @param copies {Number}
   */
  printStatsProducts: (range, sortBy, orderBy, printer = {name: ''}, copies = 1) => {
    // console.log('>>>>>>>> printOrderTotalsZ::: ', range, printer.name, copies)
    range = range || '-'
    sortBy = sortBy || 'Id'
    orderBy = orderBy || 'DESC'
    printer = clone(printer)
    if (IS_DESKTOP_APP && printer.name === '') return
    printer.copies = parseInt(copies)
    printURLtoPrinter(`${import.meta.env.VITE_API_URL}stats/products/print/${range}/${sortBy}/${orderBy}`, [printer], false)

    window.Vue && window.Vue.$bus.$emit('app-show-notification', {
      body: window.Vue.$t('Order.Print.NotificationTotals'),
      type: 'info',
      icon: 'print'
    })
  }

}

const API = axios.create({
  baseURL        : '',
  timeout        : 30000,
  responseType   : 'text',
  crossDomain    : true,
  withCredentials: true,
  headers        : {
    'X-Requested-With': 'XMLHttpRequest',
    'X-Referer'       : import.meta.env.VITE_API_X_REFERER || '',
    'X-Guid'          : DataStore.app.guid || ''
  }
})

API.interceptors.request.use((request) => {
  request.headers.Authorization = window.authorizationTokenBearer
  request.headers['Accept-Language'] = getPrintLocale()
  request.headers['X-Guid'] = DataStore.app.guid || ''
  return request
}, function (error) {
  return Promise.reject(error)
})

export default Printer
